/**
 * SquadChangesWidget - Starting XI changes between matches
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Users, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { formatSeasonDate } from './utils';

export function SquadChangesWidget({ squadChanges, avgSquadChanges }) {
  const [expandedChanges, setExpandedChanges] = useState({});

  if (!squadChanges?.length) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5 text-indigo-600" strokeWidth={1.5} />Starting XI Changes
        </CardTitle>
        <p className="text-sm text-gray-500">
          Changes to starting lineup between consecutive matches
          {avgSquadChanges > 0 && (
            <span className="ml-2 font-medium text-indigo-600">(Avg: {avgSquadChanges} per match)</span>
          )}
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-6">
          {squadChanges.map((change, idx) => (
            <div key={idx} className="p-3 sm:p-4 bg-gray-50 rounded-xl border border-gray-100" data-testid={`squad-change-${idx}`}>
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-500">
                    <span className="truncate">{change.prev_opponent}</span>
                    <ChevronRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-300 shrink-0" />
                    <span className="font-medium text-gray-900 truncate">{change.opponent}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{formatSeasonDate(change.match_date)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2 sm:ml-3">
                  {change.changes > 0 && (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-gray-500 hover:text-indigo-600"
                      onClick={() => setExpandedChanges(prev => ({ ...prev, [idx]: !prev[idx] }))}
                      data-testid={`squad-change-view-${idx}`}>
                      {expandedChanges[idx] ? <EyeOff className="w-3.5 h-3.5 mr-1" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
                      {expandedChanges[idx] ? 'Hide' : 'View'}
                    </Button>
                  )}
                  <div className="text-right">
                    <span className={`text-lg sm:text-xl font-bold ${
                      change.changes === 0 ? 'text-emerald-600' : change.changes <= 2 ? 'text-amber-600' : 'text-red-600'
                    }`}>{change.changes}</span>
                    <p className="text-xs text-gray-400">{change.changes === 1 ? 'change' : 'changes'}</p>
                  </div>
                </div>
              </div>
              {change.changes > 0 && expandedChanges[idx] && (
                <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-gray-200">
                  {change.players_in.map((name, i) => (
                    <span key={`in-${i}`} className="text-xs bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">+ {name}</span>
                  ))}
                  {change.players_out.map((name, i) => (
                    <span key={`out-${i}`} className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded">- {name}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
